import type { BarSeriesOption } from 'echarts/charts'
import type { DataZoomComponentOption, GridComponentOption, ToolboxComponentOption, TooltipComponentOption } from 'echarts/components'
import type { ComposeOption } from 'echarts/core'
import * as zod from 'zod'
import { BAR_CHART, COMMON_WIDGET_DATA_CONSTRUCTOR, COMMON_WIDGET_ELEMENT_DATA_CONSTRUCTOR, WidgetDataPresentationSchema, WidgetElementSchema, WidgetSchema, type AbstractWidgetData, type BaseWidgetElementDialogProps, type GraphWidgetElementData, type PartialWidget, type PartialWidgetElement, type WidgetConstructor, type WidgetDataPresentation, type WidgetElementConstructor } from './Common'
import type { BaseWidgetDataRequest, WidgetDataResponse } from './Widgets'
import { SortDirectionSchema } from '../AccountSettings'
import type { SortDirection } from '../Utils'
import type { DepartmentPageLine } from '../Device'
import type { SetRequiredDeep } from 'type-fest'

export type BarChartOptions = ComposeOption<
  BarSeriesOption
  | TooltipComponentOption
  | GridComponentOption
  | ToolboxComponentOption
  | DataZoomComponentOption
>
export type BarChartWidgetDataRequest = BaseWidgetDataRequest & {
  type: 'BAR_CHART'
  widget: BarChartWidget
}

export type BarChartWidgetOptionsResponse = WidgetDataResponse & {
  type: 'BAR_CHART'
  options: BarChartOptions
}

export const BarChartWidgetElementSchema = WidgetElementSchema.extend({
  type: BAR_CHART,
  color: zod.string().nullable(),
})
export type BarChartWidgetElement = zod.infer<typeof BarChartWidgetElementSchema>
export type BarChartWidgetElementPartial = SetRequiredDeep<PartialWidgetElement<BarChartWidgetElement>, 'type'>

export const BarChartWidgetSchema = WidgetSchema.extend({
  type: BAR_CHART,
  sortDirection: SortDirectionSchema,
  dataPresentation: WidgetDataPresentationSchema,
  axisLabelRotation: zod.number().default(0),
  elements: BarChartWidgetElementSchema.array(),
})
export type BarChartWidget = zod.infer<typeof BarChartWidgetSchema>
export type BarChartWidgetPartial = SetRequiredDeep<PartialWidget<BarChartWidget>, 'type' | 'elements' | `elements.${number}.type`>

export type BarChartWidgetElementData = GraphWidgetElementData
export interface BarChartWidgetData extends AbstractWidgetData<BarChartWidgetElementData> {
  type: 'BAR_CHART'
  sortDirection: SortDirection
  dataPresentation: WidgetDataPresentation
  axisLabelRotation: number
  elements: BarChartWidgetElementData[]
}

export interface BarChartWidgetElementDialogProps extends BaseWidgetElementDialogProps {
  type: 'BAR_CHART'
  line?: DepartmentPageLine
}

export const DEFAULT_BAR_CHART_WIDGET_ELEMENT_CONSTRUCTOR: WidgetElementConstructor<BarChartWidgetElementPartial> = () => ({
  ...COMMON_WIDGET_ELEMENT_DATA_CONSTRUCTOR(),
  color: null,
  type: 'BAR_CHART',
})
export const BAR_CHART_WIDGET_CONSTRUCTOR: WidgetConstructor<BarChartWidgetPartial> = () => ({
  type: 'BAR_CHART',
  ...COMMON_WIDGET_DATA_CONSTRUCTOR(),
  dataPresentation: 'COLUMNS',
  sortDirection: 'NONE',
  elements: [DEFAULT_BAR_CHART_WIDGET_ELEMENT_CONSTRUCTOR()],
})
